import type { Dashboard, Panel } from '@/types/dashboard';

function normalizePanelFormulas(panel: Panel): Panel {
  const data = panel.data as Record<string, unknown> | undefined;
  if (!data) return panel;

  if (!data.formulas && typeof data.formula === 'string') {
    const { formula, ...rest } = data;
    return { ...panel, data: { ...rest, formulas: { main: formula } } };
  }

  return panel;
}

export function normalizeDashboard(raw: Partial<Dashboard>): Dashboard {
  const panels = (raw.panels || []).map(normalizePanelFormulas);

  return {
    uuid: raw.uuid,
    sourceKey: raw.sourceKey || '',
    contextKey: raw.contextKey || '',
    viewMode: raw.viewMode || 'full',
    displayType: raw.displayType || (raw.viewMode === 'list' ? 'list' : 'single'),
    viewModeCondition: raw.viewModeCondition,
    title: raw.title || '',
    updatedAt: raw.updatedAt,
    currentUser: raw.currentUser,
    layout: raw.layout || {
      columns: 12,
      margin: [10, 10],
      cellHeight: 30
    },
    panels
  };
}

export function cleanPanelForSave(panel: Panel): Partial<Panel> {
  const { _isLoading, _loadError, _dataLoaded, ...rest } = panel;

  const cleanedData = (() => {
    if (!panel.data) return undefined;
    if ('formulas' in panel.data && panel.data.formulas) {
      return { formulas: panel.data.formulas };
    }
    return undefined;
  })();

  return {
    ...rest,
    data: cleanedData
  };
}

export function cleanDashboardForSave(dashboard: Dashboard): Omit<Partial<Dashboard>, 'panels'> & { panels: Partial<Panel>[] } {
  return {
    uuid: dashboard.uuid,
    sourceKey: dashboard.sourceKey,
    viewMode: dashboard.viewMode,
    displayType: dashboard.displayType,
    title: dashboard.title,
    viewModeCondition: dashboard.viewModeCondition,
    layout: dashboard.layout,
    panels: dashboard.panels.map(cleanPanelForSave)
  };
}
